import React, { ReactNode, useEffect, useState } from 'react';
import Icon from 'src/components/common/Icon';
import {
    CardRoot,
    CheckButton,
    Content,
    Description,
    FieldBlock,
    FieldLabel,
    FieldsGrid,
    HeaderRow,
    Helper,
    IconWrap,
    Input,
    InputWrap,
    Prefix,
    Select,
    Title,
    TitleDesc,
} from './SelectableRangeCard.styles';

export type SingleValueSection = {
    id: string;
    label: string;
    value: number;
    onChange: (next: number) => void;
    minAllowed?: number; // default 1
    maxAllowed?: number; // default 200
    prefix?: string; // e.g. "$"
    helperText?: string;
    /** Si es false, no se muestra el sufijo "(máx N)" en la etiqueta. Por defecto true. */
    showMaxInLabel?: boolean;
    /**
     * Si el input queda vacío, el mensaje de error usa este mínimo en el texto en lugar de `minAllowed`.
     * Útil cuando `minAllowed` depende de otro campo (ej. precio total = unitario × cantidad en Redux)
     * y al borrar se quiere mostrar siempre el mínimo unitario (ej. 60).
     */
    emptyFieldErrorMinDisplay?: number;
};

export type DropdownSection = {
    id: string;
    label: string;
    value: string | null;
    options: { label: string; value: string }[];
    onChange: (value: string) => void;
    /** Texto de la opción placeholder (deshabilitada). Si no se define, no se muestra placeholder. */
    placeholder?: string;
};

export type SelectableRangeCardProps = {
    selected: boolean;
    onToggle?: (next: boolean) => void;
    borderColor: string;
    backgroundColor: string;
    checkColor: string;
    icon: string | ReactNode;
    title: string;
    description: string;
    sections: SingleValueSection[];
    borderColorUnselected?: string;
    backgroundColorUnselected?: string;
    iconBgColor?: string;
    /** Si true, la carta no es clicable (no se puede seleccionar/deseleccionar) */
    disabled?: boolean;
    /** Callback cuando cambia el estado de error de una sección (id de sección + hasError). */
    onSectionError?: (sectionId: string, hasError: boolean) => void;
    /** Dropdown opcional que se muestra junto a los inputs numéricos (ej. zona para Elite, meses para slots). */
    dropdownSection?: DropdownSection;
    /** Posición del dropdown respecto a los inputs numéricos. Por defecto 'before'. */
    dropdownPlacement?: 'before' | 'after';
};

/** Genera el mensaje de error si el valor está fuera de rango, o vacío si es válido. */
const getRangeError = (value: number, min: number, max: number): string => {
    if (value < min) return `El valor mínimo es ${min}`;
    if (value > max) return `El valor máximo es ${max}`;
    return '';
};

/**
 * Input de texto que solo acepta dígitos. Usa type="text" con inputMode="numeric"
 * para tener control total del display (evita el bug de React con type="number"
 * donde "0100" no se normaliza a "100"). El campo puede quedar vacío mientras
 * el usuario escribe; solo se envía a Redux cuando hay un número válido.
 */
const NumericInput = ({
    value,
    onChange,
    minAllowed,
    maxAllowed,
    label,
    prefix,
    helperText,
    onErrorChange,
    emptyFieldErrorMinDisplay,
}: {
    value: number;
    onChange: (next: number) => void;
    minAllowed: number;
    maxAllowed: number;
    label: string;
    prefix?: string;
    helperText?: string;
    onErrorChange?: (hasError: boolean) => void;
    emptyFieldErrorMinDisplay?: number;
}) => {
    const [display, setDisplay] = useState(String(value));

    // Sincronizar cuando el valor externo cambia (ej. auto-select, reset)
    useEffect(() => {
        setDisplay(String(value));
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;

        // Permitir campo vacío (el usuario está borrando)
        if (raw === '') {
            setDisplay('');
            return;
        }

        // Solo aceptar dígitos
        if (!/^\d+$/.test(raw)) return;

        // Normalizar: quitar ceros a la izquierda ("0100" → "100")
        const normalized = String(Number(raw));
        setDisplay(normalized);
        onChange(Number(normalized));
    };

    const minForEmptyMessage = emptyFieldErrorMinDisplay ?? minAllowed;
    const error = display === ''
        ? `El valor mínimo es ${minForEmptyMessage}`
        : getRangeError(Number(display), minAllowed, maxAllowed);

    // Notificar al padre cuando el estado de error cambia
    useEffect(() => {
        onErrorChange?.(!!error);
    }, [!!error]); // eslint-disable-line react-hooks/exhaustive-deps

    const getHelperContent = () => {
        if (error) return <Helper $error>{error}</Helper>;
        if (helperText) return <Helper>{helperText}</Helper>;
        return null;
    };

    return (
        <>
            <InputWrap $error={!!error}>
                {prefix ? <Prefix>{prefix}</Prefix> : null}
                <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={display}
                    onChange={handleChange}
                    aria-label={label}
                    aria-invalid={!!error}
                />
            </InputWrap>
            {getHelperContent()}
        </>
    );
};

const SelectableRangeCard = ({
    selected,
    onToggle,
    borderColor,
    backgroundColor,
    checkColor,
    icon,
    title,
    description,
    sections,
    borderColorUnselected = '#E5E7EB',
    backgroundColorUnselected = '#FFFFFF',
    iconBgColor = '#FFF7ED',
    disabled = false,
    onSectionError,
    dropdownSection,
    dropdownPlacement = 'before',
}: SelectableRangeCardProps) => {
    const renderDropdown = () =>
        dropdownSection ? (
            <FieldBlock key={dropdownSection.id}>
                <FieldLabel>{dropdownSection.label}</FieldLabel>
                <InputWrap>
                    <Select
                        value={dropdownSection.value ?? ''}
                        onChange={(e) => dropdownSection.onChange(e.target.value)}
                        aria-label={dropdownSection.label}
                    >
                        {dropdownSection.placeholder && (
                            <option value="" disabled>
                                {dropdownSection.placeholder}
                            </option>
                        )}
                        {dropdownSection.options.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </Select>
                </InputWrap>
            </FieldBlock>
        ) : null;

    return (
        <CardRoot
            $selected={selected}
            $disabled={disabled}
            $borderColor={borderColor}
            $backgroundColor={backgroundColor}
            $borderColorUnselected={borderColorUnselected}
            $backgroundColorUnselected={backgroundColorUnselected}
        >
            <Content>
                <HeaderRow>
                    <CheckButton
                        type="button"
                        disabled={disabled}
                        aria-pressed={selected}
                        aria-label={selected ? `Deseleccionar ${title}` : `Seleccionar ${title}`}
                        $selected={selected}
                        $checkColor={checkColor}
                        $disabled={disabled}
                        onClick={() => !disabled && onToggle?.(!selected)}
                    />

                    <IconWrap $iconBgColor={iconBgColor}>
                        {typeof icon === 'string' ? <Icon icon={icon} size={16} /> : icon}
                    </IconWrap>

                    <TitleDesc>
                        <Title>{title}</Title>
                        <Description>{description}</Description>
                    </TitleDesc>
                </HeaderRow>

                {selected && (
                    <FieldsGrid>
                        {dropdownPlacement === 'before' && renderDropdown()}
                        {sections.map((section) => {
                            const minAllowed = section.minAllowed ?? 1;
                            const maxAllowed = section.maxAllowed ?? 200;

                            return (
                                <FieldBlock key={section.id}>
                                    <FieldLabel>
                                        {section.label}
                                        {section.showMaxInLabel !== false &&
                                        Number.isFinite(maxAllowed)
                                            ? ` (máx ${maxAllowed})`
                                            : ''}
                                    </FieldLabel>

                                    <NumericInput
                                        value={section.value}
                                        onChange={section.onChange}
                                        minAllowed={minAllowed}
                                        maxAllowed={maxAllowed}
                                        label={section.label}
                                        prefix={section.prefix}
                                        helperText={section.helperText}
                                        emptyFieldErrorMinDisplay={section.emptyFieldErrorMinDisplay}
                                        onErrorChange={onSectionError
                                            ? (hasError) => onSectionError(section.id, hasError)
                                            : undefined
                                        }
                                    />
                                </FieldBlock>
                            );
                        })}
                        {dropdownPlacement === 'after' && renderDropdown()}
                    </FieldsGrid>
                )}
            </Content>
        </CardRoot>
    );
};

export default SelectableRangeCard;

